import { Link } from "react-router-dom";
import React, { useState, useRef } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import "./InformationalModel1.css";

function InformationalModel1() {
  const [nodes, setNodes] = useState([
    {
      id: 1,
      name: "Company",
      x: 600,
      y: 80,
      width: 250,
      attributes: [
        { visibility: "-", name: "id", type: "int" },
        { visibility: "-", name: "name", type: "string" },
      ],
      methods: [
        { visibility: "+", name: "getName", params: "", returnType: "string" },
      ],
    },
    {
      id: 2,
      name: "Employee",
      x: 280,
      y: 260,
      width: 250,
      attributes: [
        { visibility: "-", name: "id", type: "int" },
        { visibility: "-", name: "salary", type: "float" },
      ],
      methods: [
        { visibility: "+", name: "getSalary", params: "", returnType: "float" },
      ],
    },
  ]);

  const [links, setLinks] = useState([]);
  const [zoom, setZoom] = useState(1);
  const [dragging, setDragging] = useState(null);
  const [editing, setEditing] = useState(null);
  const [connectMode, setConnectMode] = useState(false);
  const [pendingFrom, setPendingFrom] = useState(null);
  const [selected, setSelected] = useState(null);
  const svgRef = useRef(null);
  const editorRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  const HEADER_H = 25;
  const PAD = 10;
  const LINE_H = 18;
  const GAP_SEC = 10;

  const handleZoom = (e) => setZoom(parseFloat(e.target.value));

  const isInteractiveTarget = (t) => {
    const tag = t?.tagName?.toLowerCase();
    return ["input", "textarea", "select", "button"].includes(tag);
  };

  // —————————————————————————————————————
  // Drag
  const onMouseDown = (e, id) => {
    if (isInteractiveTarget(e.target)) return;
    e.stopPropagation();
    setSelected(id);
    const node = nodes.find((n) => n.id === id);
    setDragging({
      id,
      offsetX: e.clientX - node.x,
      offsetY: e.clientY - node.y,
    });
  };

  const onMouseMove = (e) => {
    if (!dragging) return;
    setNodes((prev) =>
      prev.map((n) =>
        n.id === dragging.id
          ? { ...n, x: e.clientX - dragging.offsetX, y: e.clientY - dragging.offsetY }
          : n
      )
    );
  };

  const onMouseUp = () => setDragging(null);

  // —————————————————————————————————————
  // Parsing helpers
  const parseAttrLine = (text, prev) => {
    const m = text.trim().match(/^([+\-#])?\s*([A-Za-z_]\w*)?(?:\s*:\s*([\w\[\]<>]+))?$/);
    return {
      visibility: m?.[1] || prev.visibility || "+",
      name: m?.[2] || prev.name || "attr",
      type: m?.[3] || prev.type || "type",
    };
  };

  const parseMethodLine = (text, prev) => {
    const m = text.trim().match(
      /^([+\-#])?\s*([A-Za-z_]\w*)?(?:\s*\(([^)]*)\))?(?:\s*:\s*([\w\[\]<>]+))?$/
    );
    return {
      visibility: m?.[1] || prev.visibility || "+",
      name: m?.[2] || prev.name || "method",
      params: m?.[3] ?? prev.params ?? "",
      returnType: m?.[4] || prev.returnType || "void",
    };
  };

  // —————————————————————————————————————
  // Classes
  const addClass = () => {
    const id = nodes.length ? Math.max(...nodes.map((n) => n.id)) + 1 : 1;
    setNodes([
      ...nodes,
      {
        id,
        name: `Class${id}`,
        x: 420 + id * 20,
        y: 200 + id * 20,
        width: 250,
        attributes: [],
        methods: [],
      },
    ]);
  };

  const deleteSelected = () => {
    if (!selected) return alert("Select a class to delete.");
    setLinks(links.filter((l) => l.from !== selected && l.to !== selected));
    setNodes(nodes.filter((n) => n.id !== selected));
    setSelected(null);
  };

  const renameClass = (id, newName) =>
    setNodes(nodes.map((n) => (n.id === id ? { ...n, name: newName } : n)));

  // —————————————————————————————————————
  // Attributes & Methods
  const addAttribute = (id) =>
    setNodes(
      nodes.map((n) =>
        n.id === id
          ? {
              ...n,
              attributes: [...n.attributes, { visibility: "-", name: "newAttr", type: "type" }],
            }
          : n
      )
    );

  const addMethod = (id) =>
    setNodes(
      nodes.map((n) =>
        n.id === id
          ? {
              ...n,
              methods: [...n.methods, { visibility: "+", name: "newMethod", params: "", returnType: "void" }],
            }
          : n
      )
    );

  const removeAttribute = (id, i) =>
    setNodes(
      nodes.map((n) =>
        n.id === id ? { ...n, attributes: n.attributes.filter((_, k) => k !== i) } : n
      )
    );

  const removeMethod = (id, i) =>
    setNodes(
      nodes.map((n) =>
        n.id === id ? { ...n, methods: n.methods.filter((_, k) => k !== i) } : n
      )
    );

  // —————————————————————————————————————
  // Connections
  const onClassClick = (id) => {
    if (!connectMode || connectMode === "menu") return;

    if (!pendingFrom) {
      setPendingFrom(id);
    } else if (pendingFrom !== id) {
      const chosenType = connectMode;

      setLinks((prev) => [
        ...prev,
        {
          id: prev.length + 1,
          from: pendingFrom,
          to: id,
          type: chosenType, // association | generalization | aggregation | composition
          cardFrom: "1",
          cardTo: "*",
        },
      ]);

      setPendingFrom(null);
      setConnectMode(false);
    }
  };

  const changeCardinality = (id) => {
    const newCard = prompt("Enter cardinality:", "1..*");
    if (newCard)
      setLinks(links.map((l) => (l.id === id ? { ...l, cardinality: newCard } : l)));
  };

  // —————————————————————————————————————
  // Export as PNG
  const downloadDiagram = () => {
    const svg = svgRef.current;
    const svgData = new XMLSerializer().serializeToString(svg);
    const svgBlob = new Blob([svgData], {
      type: "image/svg+xml;charset=utf-8",
    });
    const url = URL.createObjectURL(svgBlob);
    const img = new Image();

    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = svg.clientWidth * 2;
      canvas.height = svg.clientHeight * 2;
      const ctx = canvas.getContext("2d");
      ctx.scale(2, 2);
      ctx.drawImage(img, 0, 0);
      URL.revokeObjectURL(url);
      const pngUrl = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = pngUrl;
      a.download = "information-model.png";
      a.click();
    };

    img.src = url;
  };

  const exportToPDF = async () => {
    const element = editorRef.current;
    const canvas = await html2canvas(element, { scale: 2 });
    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF({
      orientation: "landscape",
      unit: "px",
      format: [canvas.width / 2, canvas.height / 2],
    });

    pdf.addImage(imgData, "PNG", 0, 0, canvas.width / 2, canvas.height / 2);
    pdf.save("information-model.pdf");
  };

  // —————————————————————————————————————
  // Update Fields
  const updateField = (id, section, index, value) => {
    setNodes((prev) =>
      prev.map((n) => {
        if (n.id !== id) return n;
        const current = section === "attributes" ? n.attributes[index] : n.methods[index];
        const parsed =
          section === "attributes"
            ? parseAttrLine(value, current)
            : parseMethodLine(value, current);
        const copy = section === "attributes" ? [...n.attributes] : [...n.methods];
        copy[index] = parsed;
        return { ...n, [section]: copy };
      })
    );
  };

  // —————————————————————————————————————
  // Layout computation
  const layoutFor = (n) => {
    const attrY0 = HEADER_H + GAP_SEC + 15;
    const methodsY0 =
      attrY0 + (n.attributes.length ? n.attributes.length * LINE_H + GAP_SEC : GAP_SEC + 6);
    const totalHeight = Math.max(
      HEADER_H + 2 * GAP_SEC + 40,
      methodsY0 + (n.methods.length ? n.methods.length * LINE_H : 0) + GAP_SEC
    );
    return { attrY0, methodsY0, totalHeight };
  };

  // —————————————————————————————————————
  // Edge points for links
  const edgePoint = (cx, cy, w, h, tx, ty) => {
    const dx = tx - cx;
    const dy = ty - cy;
    if (dx === 0 && dy === 0) return { x: cx, y: cy };
    const hw = w / 2;
    const hh = h / 2;
    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);
    if (absDx * hh > absDy * hw) {
      const scale = hw / absDx;
      return { x: cx + Math.sign(dx) * hw, y: cy + dy * scale };
    } else {
      const scale = hh / absDy;
      return { x: cx + dx * scale, y: cy + Math.sign(dy) * hh };
    }
  };

  // —————————————————————————————————————
  // High-quality PDF capture
  const handleDownloadPDF = async () => {
    try {
      const element = editorRef.current;
      if (!element) {
        alert("Error: Unable to find content to export!");
        return;
      }

      window.scrollTo(0, 0);

      const canvas = await html2canvas(element, {
        scale: 2.5,
        backgroundColor: "#ffffff",
        useCORS: true,
        logging: false,
      });

      const imgData = canvas.toDataURL("image/png", 1.0);
      const pdf = new jsPDF("l", "mm", "a4");

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      let position = 0;
      const pageHeight = pdf.internal.pageSize.getHeight();

      while (position < pdfHeight) {
        pdf.addImage(imgData, "PNG", 0, -position, pdfWidth, pdfHeight);
        position += pageHeight;
        if (position < pdfHeight) pdf.addPage();
      }

      pdf.save("informational-model.pdf");
      alert("✅ PDF downloaded successfully!");
    } catch (err) {
      console.error("Error generating PDF:", err);
      alert("🚨 Error generating PDF.");
    }
  };

  // —————————————————————————————————————
  return (
    <div className="informational-model-1-1" ref={editorRef}>
      {/* Top Bar */}
      <div className="nav-items-4-6">
        <img
          className="x1200px-logo_icam_-_2008-1-8"
          src="https://upload.wikimedia.org/wikipedia/commons/f/f2/ICAM_logo.png"
          alt="icam logo"
        />
      </div>

      <div className="icam-editor">
        {/* Sidebar */}
        <div className="icam-sidebar">
          <button className="icam-btn" onClick={addClass}>
            + Class
          </button>

          {/* Link Menu */}
          <div style={{ position: "relative" }}>
            <button
              className={`icam-btn ${connectMode ? "active" : ""}`}
              onClick={() => {
                if (connectMode && connectMode !== "menu") {
                  setConnectMode("menu");
                  setPendingFrom(null);
                } else {
                  setConnectMode(connectMode === "menu" ? false : "menu");
                  setPendingFrom(null);
                }
              }}
            >
              {connectMode === "menu"
                ? "Choose link type"
                : connectMode
                ? "Delete link"
                : "Links"}
            </button>

            {connectMode === "menu" && (
              <div
                style={{
                  position: "absolute",
                  top: "40px",
                  left: 0,
                  background: "white",
                  border: "1px solid #ccc",
                  borderRadius: "6px",
                  padding: "4px",
                  zIndex: 20,
                  boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                }}
              >
                {[
                  { label: "🔗 Association", value: "association" },
                  { label: "⬆️ Inheritance", value: "generalization" },
                  { label: "◇ Aggregation", value: "aggregation" },
                  { label: "◆ Composition", value: "composition" },
                ].map((opt) => (
                  <div
                    key={opt.value}
                    style={{
                      padding: "4px 8px",
                      cursor: "pointer",
                      fontSize: "14px",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "#eee")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "white")
                    }
                    onClick={() => {
                      setConnectMode(opt.value);
                      setPendingFrom(null);
                    }}
                  >
                    {opt.label}
                  </div>
                ))}
              </div>
            )}
          </div>

          <button className="icam-btn" onClick={deleteSelected}>
            🗑 Delete
          </button>

          <button className="icam-btn" onClick={exportToPDF}>
            📄 Save
          </button>

          <div className="icam-zoom">
            <label>Zoom</label>
            <input
              type="range"
              min="0.5"
              max="2"
              step="0.1"
              value={zoom}
              onChange={handleZoom}
            />
          </div>
        </div>

        {/* Main Canvas */}
        <div className="icam-canvas" onMouseMove={onMouseMove} onMouseUp={onMouseUp}>
          <svg ref={svgRef} width="100%" height="100%">
            {/* UML definitions */}
            <defs>
              <marker
                id="arrowhead"
                markerWidth="10"
                markerHeight="7"
                refX="10"
                refY="3.5"
                orient="auto"
                markerUnits="strokeWidth"
              >
                <path d="M0,0 L10,3.5 L0,7 Z" fill="black" />
              </marker>

              <marker
                id="triangle"
                markerWidth="20"
                markerHeight="20"
                refX="20"
                refY="10"
                orient="auto"
                markerUnits="strokeWidth"
              >
                <path
                  d="M0,0 L20,10 L0,20 Z"
                  fill="white"
                  stroke="black"
                  strokeWidth="1.5"
                />
              </marker>

              <marker
                id="diamondEmpty"
                markerWidth="40"
                markerHeight="20"
                refX="-1"
                refY="5"
                orient="auto"
                markerUnits="strokeWidth"
              >
                <path
                  d="M0,5 L5,0 L10,5 L5,10 Z"
                  fill="white"
                  stroke="black"
                  strokeWidth="1"
                />
              </marker>

              <marker
                id="diamondFilled"
                markerWidth="40"
                markerHeight="20"
                refX="-1"
                refY="5"
                orient="auto"
                markerUnits="strokeWidth"
              >
                <path
                  d="M0,5 L5,0 L10,5 L5,10 Z"
                  fill="black"
                  stroke="black"
                  strokeWidth="1"
                />
              </marker>
            </defs>

            <g transform={`scale(${zoom})`}>
              {/* UML Links */}
              {links.map((l) => {
                const a = nodes.find((n) => n.id === l.from);
                const b = nodes.find((n) => n.id === l.to);
                if (!a || !b) return null;

                const aH = layoutFor(a).totalHeight;
                const bH = layoutFor(b).totalHeight;

                const acx = a.x + a.width / 2;
                const acy = a.y + aH / 2;
                const bcx = b.x + b.width / 2;
                const bcy = b.y + bH / 2;

                const p1 = edgePoint(acx, acy, a.width, aH, bcx, bcy);
                const p2 = edgePoint(bcx, bcy, b.width, bH, acx, acy);

                let markerStart = null;
                let markerEnd = null;

                switch (l.type) {
                  case "association":
                    markerStart = null;
                    markerEnd = null;
                    break;
                  case "generalization":
                    markerEnd = "url(#triangle)";
                    break;
                  case "aggregation":
                    markerStart = "url(#diamondEmpty)";
                    break;
                  case "composition":
                    markerStart = "url(#diamondFilled)";
                    break;
                  default:
                    markerStart = null;
                    markerEnd = null;
                }

                return (
                  <g
                    key={l.id}
                    style={{ cursor: "pointer" }}
                    onClick={() => changeCardinality(l.id)}
                  >
                    <line
                      x1={p1.x}
                      y1={p1.y}
                      x2={p2.x}
                      y2={p2.y}
                      stroke="black"
                      strokeWidth="2"
                      markerStart={markerStart}
                      markerEnd={markerEnd}
                      strokeLinecap="round"
                    />

                    {/* Cardinalities */}
                    {l.type !== "generalization" && (
                      <>
                        <text
                          x={p1.x + (p2.x - p1.x) * 0.1}
                          y={p1.y + (p2.y - p1.y) * 0.1 - 5}
                          fontSize="12"
                          fill="black"
                          textAnchor="middle"
                          onClick={(e) => {
                            e.stopPropagation();
                            const newCard = prompt("Cardinality A side:", l.cardFrom || "1");
                            if (newCard) {
                              setLinks((prev) =>
                                prev.map((link) =>
                                  link.id === l.id ? { ...link, cardFrom: newCard } : link
                                )
                              );
                            }
                          }}
                        >
                          {l.cardFrom}
                        </text>

                        <text
                          x={p2.x - (p2.x - p1.x) * 0.1}
                          y={p2.y - (p2.y - p1.y) * 0.1 - 5}
                          fontSize="12"
                          fill="black"
                          textAnchor="middle"
                          onClick={(e) => {
                            e.stopPropagation();
                            const newCard = prompt("Cardinality B side:", l.cardTo || "*");
                            if (newCard) {
                              setLinks((prev) =>
                                prev.map((link) =>
                                  link.id === l.id ? { ...link, cardTo: newCard } : link
                                )
                              );
                            }
                          }}
                        >
                          {l.cardTo}
                        </text>
                      </>
                    )}
                  </g>
                );
              })}

              {/* Classes */}
              {nodes.map((n) => {
                const { attrY0, methodsY0, totalHeight } = layoutFor(n);
                return (
                  <g
                    key={n.id}
                    transform={`translate(${n.x}, ${n.y})`}
                    onMouseDown={(e) => onMouseDown(e, n.id)}
                    onClick={() => onClassClick(n.id)}
                  >
                    <rect
                      width={n.width}
                      height={totalHeight}
                      rx="5"
                      ry="5"
                      className={`icam-class-body ${
                        selected === n.id ? "selected" : ""
                      }`}
                    />

                    <rect
                      width={n.width}
                      height={HEADER_H}
                      rx="5"
                      ry="5"
                      className="icam-class-header"
                    />

                    {/* Class Name */}
                    {editing?.id === n.id && editing?.section === "className" ? (
                      <foreignObject
                        x={PAD}
                        y={4}
                        width={n.width - PAD * 2}
                        height={HEADER_H - 8}
                      >
                        <input
                          type="text"
                          value={n.name}
                          onChange={(e) => renameClass(n.id, e.target.value)}
                          onBlur={() => setEditing(null)}
                          autoFocus
                          style={{
                            width: "100%",
                            border: "none",
                            outline: "none",
                            fontWeight: "bold",
                          }}
                        />
                      </foreignObject>
                    ) : (
                      <text
                        x={PAD}
                        y={17}
                        className="icam-class-title"
                        onDoubleClick={(e) => {
                          e.stopPropagation();
                          setEditing({ id: n.id, section: "className" });
                        }}
                      >
                        {n.name}
                      </text>
                    )}

                    {/* Attributes */}
                    <text
                      x={n.width - PAD - 11}
                      y={attrY0 - 9}
                      fontSize="16"
                      fill="green"
                      style={{ cursor: "pointer" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        addAttribute(n.id);
                      }}
                    >
                      ＋
                    </text>

                    {n.attributes.map((a, i) => {
                      const y = attrY0 + i * LINE_H;
                      const editThis =
                        editing?.id === n.id &&
                        editing?.section === "attr" &&
                        editing?.index === i;

                      return (
                        <g key={i}>
                          {editThis ? (
                            <foreignObject
                              x={PAD}
                              y={y - 14}
                              width={n.width - PAD * 2 - 20}
                              height={20}
                            >
                              <input
                                type="text"
                                value={`${a.visibility} ${a.name} : ${a.type}`}
                                onChange={(e) =>
                                  updateField(n.id, "attributes", i, e.target.value)
                                }
                                onBlur={() => setEditing(null)}
                                autoFocus
                                style={{
                                  width: "100%",
                                  border: "none",
                                  outline: "none",
                                  fontSize: "12px",
                                }}
                              />
                            </foreignObject>
                          ) : (
                            <text
                              x={PAD}
                              y={y}
                              fontSize="12"
                              fill="#0f172a"
                              onDoubleClick={(e) => {
                                e.stopPropagation();
                                setEditing({ id: n.id, section: "attr", index: i });
                              }}
                            >
                              {a.visibility} {a.name} : {a.type}
                            </text>
                          )}

                          <text
                            x={n.width - PAD - 10}
                            y={y}
                            fontSize="12"
                            fill="red"
                            style={{ cursor: "pointer" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              removeAttribute(n.id, i);
                            }}
                          >
                            ✕
                          </text>
                        </g>
                      );
                    })}

                    {/* Separation line */}
                    <line
                      x1={0}
                      y1={methodsY0 - GAP_SEC - 11}
                      x2={n.width}
                      y2={methodsY0 - GAP_SEC - 11}
                      stroke="#1e293b"
                      strokeWidth="1"
                    />

                    {/* Methods */}
                    <text
                      x={n.width - PAD - 10}
                      y={methodsY0 - 8}
                      fontSize="14"
                      fill="green"
                      style={{ cursor: "pointer" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        addMethod(n.id);
                      }}
                    >
                      ＋
                    </text>

                    {n.methods.map((m, i) => {
                      const y = methodsY0 + i * LINE_H;
                      const editThis =
                        editing?.id === n.id &&
                        editing?.section === "method" &&
                        editing?.index === i;

                      return (
                        <g key={i}>
                          {editThis ? (
                            <foreignObject
                              x={PAD}
                              y={y - 14}
                              width={n.width - PAD * 2 - 20}
                              height={20}
                            >
                              <input
                                type="text"
                                value={`${m.visibility} ${m.name}(${m.params}) : ${m.returnType}`}
                                onChange={(e) =>
                                  updateField(n.id, "methods", i, e.target.value)
                                }
                                onBlur={() => setEditing(null)}
                                autoFocus
                                style={{
                                  width: "100%",
                                  border: "none",
                                  outline: "none",
                                  fontSize: "12px",
                                }}
                              />
                            </foreignObject>
                          ) : (
                            <text
                              x={PAD}
                              y={y}
                              fontSize="12"
                              fill="#0f172a"
                              onDoubleClick={(e) => {
                                e.stopPropagation();
                                setEditing({ id: n.id, section: "method", index: i });
                              }}
                            >
                              {m.visibility} {m.name}({m.params}) : {m.returnType}
                            </text>
                          )}

                          <text
                            x={n.width - PAD - 10}
                            y={y}
                            fontSize="12"
                            fill="red"
                            style={{ cursor: "pointer" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              removeMethod(n.id, i);
                            }}
                          >
                            ✕
                          </text>
                        </g>
                      );
                    })}
                  </g>
                );
              })}
            </g>
          </svg>
        </div>
      </div>

      {/* Navigation */}
      <div className="icam-navigation">
        <Link to="/decisional-model-1">
          <button className="icam-nav-btn">Back</button>
        </Link>
        <Link to="/lean-vsm-1">
          <button className="icam-nav-btn">Continue</button>
        </Link>
      </div>
    </div>
  );
}

export default InformationalModel1;
